import { useCallback, useContext, useEffect, useRef, useState } from "react";
import type { GameRuleState, SpearheadArmyId, SpearheadGameRuleState } from "whole-shared";
import { playerInTurn } from "whole-shared";

import { createCleaner } from "../../config";
import { Context, useTimeout } from "../../site";
import { ClientAosFactions } from "../aos";
import { ClientSpearheadArmies } from "../aos4spearhead";
import { match } from "../globals";
import type { ClientAbility} from "../rules";
import { sendAction } from "../rules";
import { hud } from "./HudController";

interface AbilityProps {
  ability: ClientAbility;
  onClick?: () => void;
  selected?: boolean;
  setShowTrue: () => void;
  setting: boolean;
}

const Ability = ({ ability, onClick, selected, setShowTrue, setting }: AbilityProps) => <div
  className={["army-ability", ...(setting && onClick ? ["army-ability-select"] : [])].join(" ")}
  onClick={onClick}
  onMouseEnter={() => hud.emit("tooltip", { ability: ability }, setShowTrue)}
  onMouseLeave={() => hud.emit("tooltip")}
  style={selected ? { borderColor: "white" } : {}}
>
  <b>{ability.name}</b>
</div>;

interface ArmyProps {
  abilities: number[];
  active: boolean;
  commander: number;
  setAbility: (id: number, value: number) => void;
  setShowTrue: () => void;
  setting: boolean;
  state: GameRuleState;
}

const Army = ({ abilities, active, commander, setAbility, setShowTrue, setting, state }: ArmyProps) => {
  const { displayName1, displayName2, rule } = match;
  const { player } = useContext(Context);
  const commander1 = commander === 1;
  const army = ClientSpearheadArmies[state[commander1 ? "army1p" : "army2p"] as SpearheadArmyId];
  const enabled = active && setting && player.id === (commander1 ? rule.player1Id : rule.player2Id);

  return <div className="panel" style={{ fontSize: 12, margin: 5 }}>
    <span style={{ fontFamily: "monospace" }}>{`Commander ${commander}:`}</span>
    {` ${commander1 ? displayName1 : displayName2}`}
    <br />
    <span style={{ fontFamily: "monospace" }}>{"Faction:\u00a0\u00a0\u00a0\u00a0"}</span>
    {` ${ClientAosFactions[army.faction].name}`}
    <br />
    <span style={{ fontFamily: "monospace" }}>{"Army:\u00a0\u00a0\u00a0\u00a0\u00a0\u00a0\u00a0"}</span>
    {` ${army.name}`}
    <br />
    <b>BATTLE TRAITS:</b>
    <br />
    <div style={{ display: "flex" }}>
      {army.battleTraits.map((ability, idx) => <Ability ability={ability} key={idx} selected setShowTrue={setShowTrue} setting={setting} />)}
    </div>
    <b>REGIMENT ABILITIES:</b>
    <br />
    <div style={{ display: "flex" }}>
      {army.regimentAbilities.map((ability, idx) => {
        const id = commander1 ? 0 : 2;

        return <Ability
          ability={ability}
          key={idx}
          onClick={enabled ? () => setAbility(id, idx) : undefined}
          selected={abilities[id] === idx}
          setShowTrue={setShowTrue}
          setting={setting}
        />;
      })}
    </div>
    <b>ENHANCEMENTS:</b>
    <br />
    {army.enhancements
      .reduce<[ClientAbility, number][][]>((ret, ability, idx) => {
        const last = ret.length - 1;

        if(last === -1 || ret[last].length === 2) ret.push([[ability, idx]]);
        else ret[last].push([ability, idx]);

        return ret;
      }, [])
      .map((_, idx) => <div key={idx} style={{ display: "flex" }}>
        {_.map(([ability, idx]) => {
          const id = commander1 ? 1 : 3;

          return <Ability
            ability={ability}
            key={idx}
            onClick={enabled ? () => setAbility(id, idx) : undefined}
            selected={abilities[id] === idx}
            setShowTrue={setShowTrue}
            setting={setting}
          />;
        })}
      </div>)}
  </div>;
};

export const Armies = () => {
  const { player } = useContext(Context);

  const [abilities, setAbilities] = useState<number[]>([]);
  const [setting, setSetting] = useState(false);
  const [show, setShow] = useState(false);

  const timeout = useTimeout();
  const detach = useRef<(() => void) | undefined>(undefined);

  const setShowFalse = useCallback(() => timeout.set(() => setShow(false), 1000), [timeout]);

  const setShowTrue = useCallback(() => {
    timeout.clear();
    setShow(true);
  }, [timeout]);

  useEffect(() => {
    const cleaner = createCleaner();

    hud.on("armiesSet", cleaner, setting => {
      setSetting(setting);

      if(setting) {
        const state = match.rule.state as SpearheadGameRuleState;
        const ruleAbilities = state.enhancements;

        setAbilities([ruleAbilities[0], ruleAbilities[1], ruleAbilities[2], ruleAbilities[3]]);
      }
    });

    hud.on("armiesShow", cleaner, show => {
      if(show) {
        const state = match.rule.state as SpearheadGameRuleState;

        setAbilities(state.enhancements);
        setShowTrue();
      } else setShowFalse();
    });

    hud.on("reset", cleaner, () => {
      setSetting(false);
      setShow(false);
    });

    return cleaner.clean;
  }, [setShowFalse, setShowTrue]);

  if(! setting && ! show) return null;

  const { rule } = match;
  const { dev, state } = rule;
  const { firstTurn, turn } = state;
  const inTurn = playerInTurn[firstTurn][turn];
  const active = player.id === (inTurn === 1 ? rule.player1Id : rule.player2Id);

  const setAbility = (id: number, value: number) => {
    const newAbilities = [...abilities];

    newAbilities[id] = value;
    setAbilities(newAbilities);

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if(! (dev ? newAbilities : [newAbilities[inTurn * 2 - 2], newAbilities[inTurn * 2 - 1]]).some(_ => _ === undefined)) {
      if(detach.current) detach.current();

      detach.current = hud.addChoice("Ok", sendAction({ enhancements: newAbilities }));
    }
  };

  const armiesProps = { abilities, active, setAbility, setShowTrue, setting, state };

  return <div className="panel" id="armies" onMouseEnter={setShowTrue} onMouseLeave={setShowFalse}>
    <Army {...armiesProps} commander={1} />
    <Army {...armiesProps} commander={2} />
  </div>;
};
